/**
 * Environment Configuration Utility
 *
 * This utility provides type-safe access to environment variables
 * and validates that required variables are present at runtime.
 */

import {
  appEnv,
  getEnv,
  getEnvNumber as getSharedEnvNumber,
  getEnvBool as getSharedEnvBool,
  getEnvArray as getSharedEnvArray,
  isTest,
} from '@personal-trainer-app/shared/src/utils/env';

import type { ViteEnvironment } from '@personal-trainer-app/shared/src/types';

interface EnvironmentConfig {
  // API Configuration
  apiBaseUrl: string;
  apiTimeout: number;

  // App Configuration
  appName: string;
  appVersion: string;
  appEnvironment: ViteEnvironment;

  // Authentication
  sessionTimeout: number;

  // Feature Flags
  enableDebugMode: boolean;
  enableMaintenanceMode: boolean;

  // Development Settings
  mockApi: boolean;
  logLevel: 'error' | 'warn' | 'info' | 'debug';
}

/**
 * Get environment variable with type conversion
 */
function getEnvVar(key: string, defaultValue?: string): string {
  if (defaultValue !== undefined) {
    return getEnv(key, { defaultValue }) as string;
  }
  return getEnv(key, { required: true }) as string;
}

/**
 * Get environment variable as number
 */
function getEnvNumber(key: string, defaultValue?: number): number {
  if (defaultValue !== undefined) {
    return getSharedEnvNumber(key, { defaultValue });
  }
  return getSharedEnvNumber(key, { required: true });
}

/**
 * Get environment variable as boolean
 */
function getEnvBoolean(key: string, defaultValue?: boolean): boolean {
  if (defaultValue !== undefined) {
    return getSharedEnvBool(key, { defaultValue });
  }
  return getSharedEnvBool(key, { required: true });
}

/**
 * Get environment variable as array (comma-separated)
 */
function getEnvArray(key: string, defaultValue?: string[]): string[] {
  if (defaultValue !== undefined) {
    return getSharedEnvArray(key, { defaultValue });
  }
  return getSharedEnvArray(key, { required: true });
}

/**
 * Get optional environment variable
 */
function getOptionalEnvVar(key: string): string | undefined {
  return getEnv(key);
}

/**
 * Validate environment configuration
 */
function validateEnvironment(): void {
  if (isTest()) {
    return;
  }

  const requiredVars = [
    'VITE_API_BASE_URL',
    'VITE_APP_NAME',
    'VITE_APP_VERSION',
    'VITE_APP_ENVIRONMENT',
    'VITE_REVERB_APP_KEY',
    'VITE_REVERB_HOST',
    'VITE_REVERB_PORT',
  ];

  const missingVars = requiredVars.filter((varName) => !getEnv(varName));

  if (missingVars.length > 0) {
    throw new Error(
      `Missing required environment variables: ${missingVars.join(', ')}\n` +
        'Please copy .env.example to .env and fill in the required values.'
    );
  }

  // Validate environment type
  // ViteEnvironment excluding 'test'
  const validEnvironments = ['development', 'staging', 'production'];
  const currentEnv = getEnv('VITE_APP_ENVIRONMENT');
  if (currentEnv && !validEnvironments.includes(currentEnv)) {
    throw new Error(
      `Invalid VITE_APP_ENVIRONMENT value: ${currentEnv}. ` +
        `Must be one of: ${validEnvironments.join(', ')}`
    );
  }
}

/**
 * Create and validate environment configuration
 */
function createEnvironmentConfig(): EnvironmentConfig {
  // Validate required variables first (unless in test environment)
  validateEnvironment();

  // Provide test defaults when in test environment
  const isTestEnv =
    getEnv('NODE_ENV') === 'test' || getEnv('VITEST') === 'true';

  return {
    // API Configuration
    apiBaseUrl: isTestEnv
      ? 'http://localhost:8000/api'
      : getEnvVar('VITE_API_BASE_URL'),
    apiTimeout: getEnvNumber('VITE_API_TIMEOUT', 10000),

    // App Configuration
    appName: isTestEnv
      ? 'Personal Trainer App (Test)'
      : getEnvVar('VITE_APP_NAME'),
    appVersion: isTestEnv ? '1.0.0-test' : getEnvVar('VITE_APP_VERSION'),
    appEnvironment: isTestEnv
      ? 'development'
      : (getEnvVar('VITE_APP_ENVIRONMENT') as ViteEnvironment),

    // Authentication
    sessionTimeout: getEnvNumber('VITE_SESSION_TIMEOUT', 120 * 60 * 1000), // 120 minutes (COOKIE_BASED)

    // Feature Flags
    enableDebugMode: getEnvBoolean('VITE_ENABLE_DEBUG_MODE', false),
    enableMaintenanceMode: getEnvBoolean('VITE_ENABLE_MAINTENANCE_MODE', false),

    // Development Settings
    mockApi: getEnvBoolean('VITE_MOCK_API', false),
    logLevel: getEnvVar('VITE_LOG_LEVEL', 'info') as
      'error' | 'warn' | 'info' | 'debug',
  };
}

// Create and export the configuration
export const env = createEnvironmentConfig();

// Export types for use in other modules
export type { EnvironmentConfig };

// Export utility functions for advanced use cases
export {
  getEnvVar,
  getEnvNumber,
  getEnvBoolean,
  getEnvArray,
  getOptionalEnvVar,
  validateEnvironment,
};

// Helper function for debugging. Which environment this is comes from shared's
// `appEnv()`, the one place that question is answered.
export const isDebugMode = env.enableDebugMode || appEnv('development');
